import { useQuery } from '@tanstack/react-query';
import { parseAsString, useQueryState } from 'nuqs';
import axiosInstance from '@/libs/axiosInstance';
import type { BrandStats, DashboardPeriod } from '@fluenceur/types';

interface BrandDashboardStatsParams {
  period?: DashboardPeriod;
  startDate?: string; // YYYY-MM-DD format
  endDate?: string; // YYYY-MM-DD format
  comparison?: boolean;
  programIds?: string[];
  affiliateIds?: string[];
  attributionCodes?: string[];
}

/**
 * Fetch brand dashboard statistics
 */
const fetchBrandDashboardStats = async (params: BrandDashboardStatsParams): Promise<BrandStats> => {
  const response = await axiosInstance.get('/dashboard/brands/stats', { params });
  return response.data.data;
};

/**
 * Hook to fetch brand dashboard statistics with URL state management
 */
export const useBrandDashboardStats = () => {
  // Get date range from URL state
  const [urlStartDate] = useQueryState('startDate', parseAsString);
  const [urlEndDate] = useQueryState('endDate', parseAsString);
  const [urlPeriod] = useQueryState('period', parseAsString);
  const [urlProgramIds] = useQueryState('programIds', parseAsString);
  const [urlAffiliateIds] = useQueryState('dashboardAffiliateIds', parseAsString);
  const [urlAttributionCodes] = useQueryState('dashboardAttributionCodes', parseAsString);

  // Build query params
  const params: BrandDashboardStatsParams = {
    comparison: true, // Always include comparison data
  };

  // If custom dates are provided in URL, use them
  if (urlStartDate && urlEndDate) {
    params.startDate = urlStartDate;
    params.endDate = urlEndDate;
    params.period = 'custom';
  } else {
    // Use URL period or default to this_month
    params.period = (urlPeriod as DashboardPeriod) || 'this_month';
  }

  // If program IDs are provided in URL, use them
  if (urlProgramIds) {
    params.programIds = urlProgramIds.split(',').filter(Boolean);
  }

  if (urlAffiliateIds) {
    params.affiliateIds = urlAffiliateIds.split(',').filter(Boolean);
  }

  if (urlAttributionCodes) {
    params.attributionCodes = urlAttributionCodes.split(',').filter(Boolean);
  }

  return useQuery({
    queryKey: ['brand-dashboard-stats', params],
    queryFn: () => fetchBrandDashboardStats(params),
    staleTime: 1000 * 60 * 5, // Consider data stale after 5 minutes
    gcTime: 1000 * 60 * 10, // Keep in cache for 10 minutes
  });
};
